import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, Play, ShieldCheck, FileX, Maximize, Printer, ScanLine } from 'lucide-react';
import PaperSheetSubmit from '../../features/paper-sheet/PaperSheetSubmit';
import { token as authToken } from '../../services/studentAi';
import { primeSheetExam } from '../../features/paper-sheet/sheetModel';
import toast from 'react-hot-toast';
import QuizInterface from '../../components/quiz/QuizInterface';
import { API_CONFIG } from '../../config/api.config';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const OnlineExamInterface: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [examData, setExamData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // States cho Anti-cheat
  const [examStarted, setExamStarted] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  // Mobile browsers (iOS Safari, Zalo in-app, Android in-app) không hỗ trợ Fullscreen API
  // → khởi tạo isFullscreen = true để bypass overlay chặn thi trên mobile
  const [isFullscreen, setIsFullscreen] = useState(() => !document.fullscreenEnabled);
  // Chỉ cảnh báo "thoát toàn màn hình" SAU KHI đã thực sự vào fullscreen ít nhất 1 lần.
  // Tránh báo sai khi fullscreen chưa từng bật (desktop từ chối / container chưa mount).
  const [enteredFullscreen, setEnteredFullscreen] = useState(false);
  const examContainerRef = useRef<HTMLDivElement>(null);
  const [paperOpen, setPaperOpen] = useState(false);   // nộp bằng phiếu đáp án giấy
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const response = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/exam/${slug}`);
        if (!response.ok) {
          throw new Error('Không tìm thấy bài thi hoặc bài thi chưa được mở.');
        }
        const result = await response.json();
        if (result.success && result.data) {
          const data = result.data;
          if (slug) primeSheetExam(slug, data);   // nút "Nộp bằng phiếu giấy" dùng lại, không tải lại đề

          // --- KIỂM TRA THỜI GIAN MỞ/ĐÓNG ĐỀ ---
          const now = new Date();
          if (data.settings?.start_time) {
            const startTime = new Date(data.settings.start_time);
            if (now < startTime) throw new Error(`Bài thi chưa mở. Thời gian mở: ${startTime.toLocaleString()}`);
          }
          if (data.settings?.end_time) {
            const endTime = new Date(data.settings.end_time);
            if (now > endTime) throw new Error(`Bài thi đã kết thúc vào: ${endTime.toLocaleString()}`);
          }

          // --- KIỂM TRA SỐ LƯỢT THI ---
          if (data.settings?.max_attempts && data.settings.max_attempts > 0) {
            const token = authToken();
            if (!token) throw new Error('Vui lòng đăng nhập để kiểm tra số lượt thi của bạn.');
            
            const attemptRes = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/check-attempts/${data.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (attemptRes.ok) {
              const attemptData = await attemptRes.json();
              if (attemptData.data && !attemptData.data.can_attempt) {
                throw new Error(`Bạn đã hết lượt làm bài (Đã thi ${attemptData.data.attempts_count}/${attemptData.data.max_attempts} lượt).`);
              }
            }
          }

          // Transform data format cho QuizInterface
          const questions = data.parts?.flatMap((p: any) => p.questions.map((q: any) => {
            // Lưu lại part title và description vào câu hỏi
            q.partTitle = p.title;
            q.partDescription = p.intro_markdown;

            // Đối với câu hỏi Đúng/Sai (dung_sai), Backend PMaker lưu nội dung ở cau_hoi
            // nhưng Ebook TrueFalseQuestion component cần question_lead
            if ((q.type === 'dung_sai' || q.type === 'true_false') && !q.question_lead) {
              q.question_lead = q.cau_hoi;
            }

            // Đảm bảo lua_chon là mảng (SQLite có thể trả về string).
            // Đề MỚI: mỗi phương án là {id, content} — option_id ổn định đi cùng nội dung, GIỮ
            // NGUYÊN dạng object (không làm phẳng về string) để chấm điểm/xáo trộn theo id thay
            // vì theo chữ cái/vị trí. Đề CŨ: lua_chon vẫn là string[] thuần — hành vi giữ nguyên.
            const normalizeChoices = (parsed: any) => {
              if (!(Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null)) {
                return parsed;
              }
              if (parsed[0].id != null) {
                return parsed.map((c: any) => ({ id: c.id, content: c.content ?? c.text ?? '' }));
              }
              return parsed.map((c: any) => c.text || c.content || JSON.stringify(c));
            };
            if (typeof q.lua_chon === 'string') {
              try { q.lua_chon = normalizeChoices(JSON.parse(q.lua_chon)); } catch(e) {}
            } else if (Array.isArray(q.lua_chon)) {
              q.lua_chon = normalizeChoices(q.lua_chon);
            }

            if (typeof q.goi_y === 'string') {
              try { q.goi_y = JSON.parse(q.goi_y); } catch(e) {}
            }
            if (typeof q.examMetadata === 'string') {
              try { q.examMetadata = JSON.parse(q.examMetadata); } catch(e) {}
            }
            // Transform dap_an_dung -> dapAnDung cho frontend
            if (q.dap_an_dung) {
                q.dapAn = q.dap_an_dung;
            }
            return q;
          })) || [];
          
          // --- LUẬT THI: XÁO TRỘN (cấu hình RIÊNG cho từng Phần I/II/III/IV) ---
          let finalQuestions = [...questions];

          const PART_KEYS = ['TN', 'DS', 'TL', 'TL_ESSAY'] as const;
          type PartKey = typeof PART_KEYS[number];
          const getPartKey = (q: any): PartKey => {
            const type = String(q.type || q.loai || '').toLowerCase();
            if (type.includes('trac_nghiem') || type.includes('multiple_choice') || q.examMetadata?.group === 'TN') return 'TN';
            if (type.includes('dung_sai') || type.includes('true_false') || q.examMetadata?.group === 'DS') return 'DS';
            if (type.includes('tinh_toan') || type.includes('short_answer') || type.includes('tra_loi_ngan') || q.examMetadata?.group === 'TL') return 'TL';
            return 'TL_ESSAY';
          };
          // Tương thích ngược: đề CŨ lưu shuffle_questions/shuffle_options là boolean áp dụng
          // CHUNG cả đề. Đề MỚI lưu object {TN,DS,TL,TL_ESSAY} — bật/tắt riêng từng Phần.
          const normalizePartFlags = (raw: any): Record<PartKey, boolean> => {
            if (raw && typeof raw === 'object') {
              return { TN: !!raw.TN, DS: !!raw.DS, TL: !!raw.TL, TL_ESSAY: !!raw.TL_ESSAY };
            }
            const v = !!raw;
            return { TN: v, DS: v, TL: v, TL_ESSAY: v };
          };
          const shuffleQuestionsCfg = normalizePartFlags(data.settings?.shuffle_questions);
          const shuffleOptionsCfg = normalizePartFlags(data.settings?.shuffle_options);

          // 1. Shuffle options (Đảo phương án TN / đảo mệnh đề Đúng-Sai) — theo từng Phần
          finalQuestions = finalQuestions.map(q => {
            const part = getPartKey(q);

            if (part === 'TN' && shuffleOptionsCfg.TN && Array.isArray(q.lua_chon) && q.lua_chon.length > 0) {
              const isNewFormat = typeof q.lua_chon[0] === 'object' && q.lua_chon[0] !== null && q.lua_chon[0].id != null;
              if (isNewFormat) {
                // option_id đi CÙNG nội dung ngay trong object — xáo trộn chỉ đảo VỊ TRÍ mảng,
                // dap_an_dung (option_id) giữ nguyên không đổi. Không cần tính lại chữ cái hay
                // ánh xạ gì — đây chính là lý do dùng option_id thay vì chữ cái để chấm điểm.
                const shuffled = [...q.lua_chon];
                for (let i = shuffled.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                q.lua_chon = shuffled;
              } else {
                // Đề CŨ (lua_chon là string[] thuần, chưa có option_id) — giữ nguyên cơ chế
                // letter-based + shuffleLetterMap để tương thích ngược, không đổi hành vi.
                const correctChar = q.dapAn || q.dap_an_dung;
                let correctIndex = -1;
                if (correctChar === 'A') correctIndex = 0;
                else if (correctChar === 'B') correctIndex = 1;
                else if (correctChar === 'C') correctIndex = 2;
                else if (correctChar === 'D') correctIndex = 3;

                const correctContent = correctIndex >= 0 ? q.lua_chon[correctIndex] : null;
                const LETTERS = ['A', 'B', 'C', 'D'];
                const indexedChoices = q.lua_chon.map((text: string, index: number) => ({
                  text,
                  originalLetter: LETTERS[index]
                }));

                for (let i = indexedChoices.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [indexedChoices[i], indexedChoices[j]] = [indexedChoices[j], indexedChoices[i]];
                }

                q.lua_chon = indexedChoices.map(item => item.text);
                q.shuffleLetterMap = indexedChoices.map(item => item.originalLetter);
                if (correctContent != null) {
                  const newIndex = q.lua_chon.indexOf(correctContent);
                  q.dapAn = LETTERS[newIndex];
                  q.dap_an_dung = q.dapAn;
                }
              }
            }

            if (part === 'DS' && shuffleOptionsCfg.DS) {
              // Chỉ đảo mệnh đề CÓ nội dung. 'key' (a/b/c/d) là cột DB cố định — giống option_id,
              // KHÔNG đổi khi đảo hiển thị, nên nộp bài/chấm điểm không bị lệch.
              const order = ['a', 'b', 'c', 'd'].filter(l => (q[`statement_${l}`] || '').toString().trim());
              for (let i = order.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [order[i], order[j]] = [order[j], order[i]];
              }
              q.statementOrder = order;
            }

            return q;
          });

          // 2. Shuffle questions (Đảo vị trí câu hỏi) — theo từng Phần, câu chùm cùng cluster_id
          // được xáo trộn như 1 đơn vị, không trộn lẫn giữa các Phần khác nhau.
          const unitsByPart: Record<PartKey, any[][]> = { TN: [], DS: [], TL: [], TL_ESSAY: [] };
          const clusterUnits = new Map<string, any[]>();
          const clusterPartOf = new Map<string, PartKey>();
          for (const q of finalQuestions) {
            const cid = q.examMetadata?.cluster_id;
            const part = getPartKey(q);
            if (cid) {
              if (!clusterUnits.has(cid)) {
                const unit: any[] = [];
                clusterUnits.set(cid, unit);
                clusterPartOf.set(cid, part);
                unitsByPart[part].push(unit);
              }
              clusterUnits.get(cid)!.push(q);
            } else {
              unitsByPart[part].push([q]);
            }
          }
          for (const part of PART_KEYS) {
            if (!shuffleQuestionsCfg[part]) continue;
            const arr = unitsByPart[part];
            for (let i = arr.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [arr[i], arr[j]] = [arr[j], arr[i]];
            }
          }
          finalQuestions = PART_KEYS.flatMap(part => unitsByPart[part].flat());

          // 3. Phân loại và sắp xếp lại theo thứ tự Nhóm để Ebook có thể render các Phần (Trắc nghiệm -> Đúng/Sai -> Trả lời ngắn)
          const getGroupWeight = (q: any) => {
              const type = q.type || q.loai || '';
              if (type.includes('trac_nghiem') || type.includes('multiple_choice') || q.examMetadata?.group === 'TN') return 1;
              if (type.includes('dung_sai') || type.includes('true_false') || q.examMetadata?.group === 'DS') return 2;
              if (type.includes('tinh_toan') || type.includes('short_answer') || type.includes('tra_loi_ngan') || q.examMetadata?.group === 'TL') return 3;
              return 4; // Tự luận và Khác
          };
          
          // Stable sort by group weight to preserve shuffle order within groups
          finalQuestions.sort((a, b) => getGroupWeight(a) - getGroupWeight(b));
          
          setExamData({
            id: data.id,
            tieuDe: data.title,
            ten: data.title,
            gioi_han_thoi_gian: data.settings?.duration ? data.settings.duration * 60 : 2700, // Sử dụng duration từ pmaker
            cauHoi: finalQuestions,
            settings: data.settings,
            parts: data.parts // Giữ lại parts nếu QuizInterface cần
          });
        } else {
          throw new Error('Dữ liệu bài thi không hợp lệ.');
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchExam();
    }
  }, [slug]);

  // Anti-cheat: Tab switch detection
  // Quét mã QR in trên phiếu (?nop=phieu) → mở thẳng "Nộp bằng phiếu giấy". Trong Zalo/Facebook thì
  // KHÔNG tự mở: đăng nhập Google bị chặn ở trình duyệt trong app → để HS bấm "Mở bằng Chrome" trước
  // (openInChrome giữ nguyên ?nop=phieu nên sang Chrome vẫn tự mở).
  useEffect(() => {
    if (!examData || searchParams.get('nop') !== 'phieu') return;
    if (/Zalo|FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|Line\//i.test(navigator.userAgent)) return;
    setPaperOpen(true);
  }, [examData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // LUÔN đếm + cảnh báo khi rời màn hình (chuyển ứng dụng, chụp ảnh gửi AI…); số lần gửi kèm bài nộp
    // cho giáo viên xem. Lời nhắc "3 lần tự nộp" chỉ khi đề bật tab_switch_warning.
    if (!examStarted || examSubmitted) return;
    const strict = !!examData?.settings?.tab_switch_warning;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount(prev => {
          const newCount = prev + 1;
          if (!strict) {
            toast.error(`CẢNH BÁO: Em vừa rời màn hình làm bài (lần ${newCount}). Số lần rời màn hình được ghi lại và gửi cho giáo viên.`, {
              duration: 5000,
              icon: '🚨'
            });
          } else if (newCount >= 3) {
            toast.error('Bạn đã rời khỏi trang quá 3 lần. Bài thi sẽ tự động nộp!', { duration: 5000 });
            // Logic auto submit sẽ được trigger ở QuizInterface
            // Hiện tại ta show cảnh báo lớn
          } else {
            toast.error(`CẢNH BÁO VI PHẠM LẦN ${newCount}: Bạn vừa rời khỏi bài thi! Nếu vi phạm 3 lần bài sẽ tự nộp.`, {
              duration: 5000,
              icon: '🚨'
            });
          }
          return newCount;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [examStarted, examSubmitted, examData]);

  // Anti-cheat: Fullscreen Lock
  useEffect(() => {
    // Trên mobile không hỗ trợ fullscreen API → không đăng ký listener
    if (!document.fullscreenEnabled) return;

    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (isFull) setEnteredFullscreen(true);
      if (enteredFullscreen && examStarted && !examSubmitted && examData?.settings?.require_fullscreen && !isFull) {
        toast.error('CẢNH BÁO: Vui lòng bật Toàn màn hình để tiếp tục làm bài!');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [examStarted, examData, examSubmitted, enteredFullscreen]);

  // Anti-cheat: Prevent Copy / Right click
  useEffect(() => {
    if (!examData?.settings?.prevent_copy) return;

    const handleContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    const handleCopy = (e: ClipboardEvent) => { e.preventDefault(); toast.error('Hành động sao chép bị vô hiệu hóa!'); };
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'p' || e.key === 's')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [examData]);

  const requestFullscreen = async () => {
    // Không hỗ trợ API → bypass (mobile/in-app browser)
    if (!document.fullscreenEnabled) {
      setIsFullscreen(true);
      return;
    }
    if (examContainerRef.current) {
      try {
        await examContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
        setEnteredFullscreen(true);
      } catch (err) {
        // API tồn tại nhưng request bị từ chối (Zalo/Samsung browser...) → bypass luôn
        setIsFullscreen(true);
        toast('Trình duyệt không hỗ trợ toàn màn hình — bài thi vẫn tiếp tục bình thường.', { icon: 'ℹ️' });
      }
    }
  };

  const startExam = async () => {
    if (examData?.settings?.require_fullscreen) {
      await requestFullscreen();
    }
    setExamStarted(true);
  };

  // Dự phòng: nếu vẫn đang trong in-app browser (redirect ở index.html thất bại / iOS không có Chrome)
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isInAppBrowser = /Zalo|FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|Line\//i.test(ua);
  const openInChrome = () => {
    const target = location.host + location.pathname + (location.search || '');
    if (/Android/i.test(ua)) {
      window.location.href = 'intent://' + target +
        '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' +
        encodeURIComponent(location.href) + ';end;';
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      window.location.href = 'googlechromes://' + target;
    }
  };
  const copyExamLink = () => {
    navigator.clipboard?.writeText(location.href);
    toast.success('Đã sao chép link. Mở Chrome rồi dán vào để vào thi.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !examData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Lỗi truy cập</h1>
        <p className="text-gray-600 mb-6">{error || 'Không tìm thấy bài thi'}</p>
        <button onClick={() => navigate('/trac-nghiem')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Quay lại danh sách
        </button>
      </div>
    );
  }

  // Màn hình chờ (Intro)
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-blue-600 p-8 text-center text-white">
            <h1 className="text-3xl font-bold mb-2">{examData.tieuDe}</h1>
            <p className="opacity-90">{examData.cauHoi.length} Câu hỏi • {Math.floor(examData.gioi_han_thoi_gian / 60)} Phút</p>
          </div>
          
          <div className="p-8 space-y-6">
            {isInAppBrowser && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-800 text-sm space-y-3">
                <p className="font-bold">⚠️ Bạn đang mở trong Zalo/Facebook. Hãy mở bằng Chrome để vào thi ổn định (đăng nhập & toàn màn hình mới hoạt động).</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={openInChrome} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold">
                    Mở bằng Chrome
                  </button>
                  <button onClick={copyExamLink} className="flex-1 py-2.5 bg-white border border-red-300 text-red-700 rounded-lg font-semibold hover:bg-red-100">
                    Sao chép link
                  </button>
                </div>
                <p className="text-xs text-red-600">Nếu nút trên không mở được: bấm dấu <b>⋮</b> (góc phải) → <b>Mở trong trình duyệt</b>.</p>
              </div>
            )}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-800 text-sm space-y-2">
              <h3 className="font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> NỘI QUY PHÒNG THI:</h3>
              <ul className="list-disc pl-6 space-y-1">
                {examData.settings?.require_fullscreen && <li>Bài thi yêu cầu chế độ Toàn màn hình (Fullscreen). Nếu thoát ra sẽ bị cảnh báo.</li>}
                {examData.settings?.tab_switch_warning && <li>Tuyệt đối <b>không chuyển Tab</b> hoặc thu nhỏ trình duyệt. Vi phạm 3 lần hệ thống tự động nộp bài.</li>}
                <li>Mỗi lần <b>rời màn hình làm bài</b> (chuyển ứng dụng, chụp ảnh hỏi AI…) đều bị cảnh báo và <b>ghi lại cho giáo viên</b>.</li>
                {examData.settings?.prevent_copy && <li>Các phím tắt sao chép và Click chuột phải đã bị vô hiệu hóa.</li>}
                <li>Thời gian sẽ bắt đầu đếm ngược ngay khi bạn nhấn "Bắt đầu làm bài".</li>
              </ul>
            </div>

            <button 
              onClick={startExam}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-200 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            >
              <Play className="w-5 h-5" /> BẮT ĐẦU LÀM BÀI
            </button>

            {/* Làm trên giấy: in phiếu đáp án → làm bài → chụp phiếu để chấm */}
            <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 space-y-3">
              <p className="text-sm text-teal-900"><b>Làm bài trên giấy?</b> Ghi đáp án vào phiếu rồi chụp phiếu để chấm điểm và xem hướng dẫn giải.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <a href={`/exam/${slug}/phieu`} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-white border border-teal-300 text-teal-800 font-bold hover:bg-teal-50">
                  <Printer className="w-4 h-4" /> In phiếu đáp án
                </a>
                <button onClick={() => setPaperOpen(true)} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700">
                  <ScanLine className="w-4 h-4" /> Nộp bằng phiếu giấy
                </button>
              </div>
            </div>
          </div>
        </div>
        {paperOpen && slug && <PaperSheetSubmit slug={slug} initialCode={searchParams.get('ma')} initialToken={searchParams.get('k')} onClose={() => setPaperOpen(false)} />}
      </div>
    );
  }

  // Màn hình làm bài (Sử dụng Light Mode - forceDarkMode=false)
  return (
    <div 
      ref={examContainerRef} 
      className="min-h-screen h-screen overflow-y-auto bg-amber-50/30 relative"
    >

      {/* Cảnh báo mất fullscreen */}
      {enteredFullscreen && !isFullscreen && examData.settings?.require_fullscreen && !examSubmitted && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md text-center border-2 border-red-500">
            <Maximize className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-600 mb-2">BẠN ĐÃ THOÁT TOÀN MÀN HÌNH</h2>
            <p className="text-gray-700 mb-6">Bạn phải quay lại chế độ Toàn màn hình để tiếp tục làm bài!</p>
            <button 
              onClick={requestFullscreen}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg"
            >
              Tiếp tục làm bài
            </button>
          </div>
        </div>
      )}

      {/* Render Quiz Interface với Force Light Mode */}
      <div className={enteredFullscreen && !isFullscreen && examData.settings?.require_fullscreen && !examSubmitted ? 'pointer-events-none opacity-0' : ''}>
        <QuizInterface
          quizData={examData}
          lessonId={slug || 'online-exam'}
          onSubmitted={() => setExamSubmitted(true)}
          onBack={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            }
            navigate('/trac-nghiem');
          }} 
          forceDarkMode={false}
          requireFullProfile={false}
          leaveCount={tabSwitchCount}
        />
      </div>
    </div>
  );
};

export default OnlineExamInterface;
